import { useEffect, useState } from "react";
import "dayjs/locale/ja";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/types";
import { Button, Form, Table } from "antd";
import { useTranslation } from "react-i18next";
import { useCreateAccountMutation, useDeleteAccountMutation, useGetAccountsQuery, useUpdateAccountMutation } from "../../../services/account";
import Icon from "../../../components/Icon";
import globalUtils from "../../../utils/globalUtils";
import tableUtils from "../../../utils/tableUtils";
import { LoadingOutlined } from "@ant-design/icons";
import { ChartOfAccountsColumn } from "../../../shared/table/columns";
import HrnModal from "../../../components/HrnModal";
import { Actions } from "../../../shared/constants";
import validateMessages from "../../../utils/validate";
import AccountForm from "./AccountForm";
import { useGetLedgerTypesQuery } from "../../../services/misc";

const ChartOfAccount = () => {
    const { t } = useTranslation();
    const { loggedInUser } = useSelector((state: RootState) => state.global);
    const [accountForm] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState("");
    const [editId, setEditId] = useState("");
    const [accounts, setAccounts] = useState<any[]>([]);
    const [accountGroups, setAccountGroups] = useState<any[]>([]);
    const [ledgerTypes, setLedgerTypes] = useState<any[]>([]);
    const companyId = loggedInUser?.currentCompany?.id;
    const {
        data: getAccountsResponse,
        isFetching: isAccountsFetching,
        isSuccess: isAccountsSuccess,
    } = useGetAccountsQuery(
        { companyId: companyId }
    );

    useEffect(() => {
        if (isAccountsSuccess) {
            const accountList = getAccountsResponse?.data || [];
            setAccounts(accountList);
            setAccountGroups(accountList.filter((x: any) => x.isGroup === true))
        }
    }, [isAccountsFetching]);

    const {
        data: getLedgerTypesResponse,
        isFetching: isLedgerTypesFetching,
        isSuccess: isLedgerTypesSuccess,
    } = useGetLedgerTypesQuery({});

    useEffect(() => {
        if (isLedgerTypesSuccess) {
            const ledgerTypeList = getLedgerTypesResponse || [];
            setLedgerTypes(ledgerTypeList);
        }
    }, [isLedgerTypesFetching]);

    const [createAccount, createAccountResult] = useCreateAccountMutation();
    const [updateAccount, updateAccountResult] = useUpdateAccountMutation();
    const [deleteAccount] = useDeleteAccountMutation();

    const showModal = () => {
        setMode(Actions.CREATE);
        setOpen(true);
    };

    const hideModal = () => {
        setOpen(false);
        accountForm.resetFields();
        setMode("");
    };

    const handleEdit = (record: any) => {
        setMode(Actions.EDIT);
        setOpen(true);
        if (record.id) setEditId(record.id);
        accountForm.setFieldsValue(record);
    };

    const handleDelete = async (record: any) => {
        try {
            await deleteAccount({companyId: companyId, id: record.id }).unwrap();
        } catch (e) { }
    };
    const onSave = async () => {
        try {
            await accountForm.validateFields();
        } catch (errorInfo) {
            return;
        }

        const formFields = accountForm.getFieldsValue();       
        if (mode === Actions.CREATE) {
            try {
                await createAccount({ companyId: companyId, body: formFields }).unwrap();
                setOpen(false);
                accountForm.resetFields();
            } catch (e) { }
        } else {
            try {
                await updateAccount({ companyId: companyId, id: editId, body: formFields }).unwrap();
                setOpen(false);
                accountForm.resetFields();
            } catch (e) { }
        }
    };

    return (
        <>
            <div className="flex justify-between items-center gap-3 pb-6">
                <div className="text-headline">{"Chart of accounts"}</div>
                <Button type="primary" size="large" className="flex-end" onClick={showModal} >
                    <div className="flex items-center gap-1.5">
                        <Icon icon="add" className="h-4.5 w-4.5" />
                        <div>{"Add Account"}</div>
                    </div>
                </Button>
            </div>
            <div className="flex justify-between flex-wrap items-center gap-4 pb-4">
                <div className="text-tertiary">
                    {""}
                </div>
                <div className="flex items-center gap-x-4">
                    <div className="font-medium">
                        {accounts?.length}{" "}
                        {globalUtils.checkIsSingular(accounts.length)
                            ? "Account"
                            : "Accounts"}
                    </div>
                </div>
            </div>
            <div>
                <Table
                    rowKey="id"
                    columns={tableUtils.buildColumns(
                        isAccountsSuccess ? accounts : [],
                        ChartOfAccountsColumn,
                        tableUtils.editDeleteActionColumns(handleEdit, handleDelete),
                        undefined,
                        undefined,
                        { serialNo: true }
                    )}
                    dataSource={accounts}
                    size="middle"
                    className="ant_table"
                    loading={{
                        spinning: isAccountsFetching as boolean,
                        indicator: <LoadingOutlined style={{ fontSize: 34 }} />,
                    }}
                    pagination={false}
                />
            </div>
            <div>
                <HrnModal
                    open={open}
                    title={`${mode === Actions.CREATE ? "Add" : "Edit"} Account`}
                    width={520}
                    isLoading={
                        (createAccountResult.isLoading ||
                            updateAccountResult.isLoading) as boolean
                    }
                    onCancel={hideModal}
                    onSave={onSave}
                >
                    <div className="mt-6">
                        <Form
                            form={accountForm}
                            layout="vertical"
                            name="accountForm"
                            validateMessages={validateMessages}
                        >
                            <AccountForm mode={mode} accountForm={accountForm} groups={accountGroups} ledgerTypes={ledgerTypes} />
                        </Form>
                    </div>
                </HrnModal>
            </div>
        </>
    );
};

export default ChartOfAccount;
